// ============================================================================
// mealConfig.js — single source of truth for resolving a subscriber's
// meal TYPE and delivery LOCATION for a specific slot on a specific date.
//
// Used by SubscriberManagement (editing/display) and Deliveries (print + sheet).
// Keeping this in one file means the two screens can never disagree.
//
// PRECEDENCE (most specific wins), resolved INDEPENDENTLY for type and location:
//   one-off date override  >  weekday rule  >  slot default  >  flat legacy field
//
// BACKWARD COMPATIBILITY:
//   Existing subscribers have flat `type` and `deliveryArea` and no slotType/
//   slotLocation/locationMode. Every resolver falls back to those flat values,
//   so old records keep working untouched until they're next edited.
// ============================================================================

export const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
export const LOCATION_LABELS = ["Home", "Office"];

// JS getDay() -> weekday label
const dayLabel = (dateStr) => WEEKDAYS[new Date(dateStr + "T00:00:00").getDay()];
const isWeekend = (dateStr) => {
  const d = new Date(dateStr + "T00:00:00").getDay();
  return d === 0 || d === 6; // Sun or Sat
};

// ── TYPE ─────────────────────────────────────────────────────────────────────
// slotType shape: { Lunch: { default: "Veg", byDay: { Thu: "Veg" }, byDate: {} } }
export function resolveType(subscriber, slot, dateStr) {
  const cfg = (subscriber.slotType || {})[slot];
  if (!cfg) return subscriber.type || "";           // legacy fallback

  // 1. one-off exact date (UI reserved for later; model supported now)
  if (cfg.byDate && cfg.byDate[dateStr]) return cfg.byDate[dateStr];
  // 2. weekday rule
  const wd = dayLabel(dateStr);
  if (cfg.byDay && cfg.byDay[wd]) return cfg.byDay[wd];
  // 3. slot default
  if (cfg.default) return cfg.default;
  // 4. legacy fallback
  return subscriber.type || "";
}

// ── LOCATION ─────────────────────────────────────────────────────────────────
// Only two location kinds exist: "Home" and "Office".
// slotLocation shape: { Lunch: { default: "Office", weekendHome: true, byDate: {} } }
// Returns { kind, area, label } where:
//   - single mode: kind = null, area = deliveryArea, label = area
//   - dual mode:   kind = "Home"|"Office", area = homeArea|officeArea,
//                  label = "Home – <area>" / "Office – <area>"
export function resolveLocation(subscriber, slot, dateStr) {
  const dual = subscriber.locationMode === "dual";

  if (!dual) {
    const area = subscriber.deliveryArea || "";
    return { kind: null, area, label: area };
  }

  const cfg = (subscriber.slotLocation || {})[slot] || {};
  let kind = cfg.default || "Office";

  // 2. weekend-home preset (Sat/Sun -> Home) — before one-off so a date flip can override it
  if (cfg.weekendHome && isWeekend(dateStr)) kind = "Home";
  // 1. one-off exact date wins over everything (UI reserved for later)
  if (cfg.byDate && cfg.byDate[dateStr]) kind = cfg.byDate[dateStr];

  const area = kind === "Home"
    ? (subscriber.homeArea || subscriber.deliveryArea || "")
    : (subscriber.officeArea || subscriber.deliveryArea || "");

  return { kind, area, label: `${kind} – ${area}` };
}

// Convenience: the printed location string for a slot on a date.
export function locationLabel(subscriber, slot, dateStr) {
  return resolveLocation(subscriber, slot, dateStr).label;
}

// ── MIGRATION HELPER ──────────────────────────────────────────────────────────
// When an operator first edits an old subscriber, seed the new structures from
// the flat fields so the editor opens pre-filled and consistent.
export function ensureConfig(subscriber, mealSlots) {
  const slots = mealSlots && mealSlots.length ? mealSlots : (subscriber.mealSlots || ["Lunch"]);
  const s = { ...subscriber };

  if (!s.locationMode) s.locationMode = "single";

  if (!s.slotType) {
    s.slotType = {};
    slots.forEach(sl => { s.slotType[sl] = { default: s.type || "Veg", byDay: {}, byDate: {} }; });
  }
  if (!s.slotLocation) {
    s.slotLocation = {};
    slots.forEach(sl => { s.slotLocation[sl] = { default: "Office", weekendHome: false, byDate: {} }; });
  }
  if (s.locationMode === "dual") {
    if (!s.homeArea)   s.homeArea   = s.deliveryArea || "";
    if (!s.officeArea) s.officeArea = s.deliveryArea || "";
  }
  return s;
}

// Keep a subscriber's flat `type`/`deliveryArea` roughly in sync for any legacy
// screen that still reads them (dashboards, filters). Uses each slot's default.
export function syncFlatFields(subscriber) {
  const s = { ...subscriber };
  const firstSlot = (s.mealSlots || ["Lunch"])[0];
  if (s.slotType && s.slotType[firstSlot]?.default) s.type = s.slotType[firstSlot].default;
  if (s.locationMode === "dual") {
    // leave deliveryArea as-is for legacy filters; dual info lives in slotLocation
  }
  return s;
}
